import dayjs from 'dayjs';
import { buildBeforeAfterPairs } from './photos/cranialPhotoViews';

/**
 * Cranial Asymmetry Report Adapter
 * 
 * Transforms cranial asymmetry data into the generic reportData format
 * used by clinicalReportService.
 */

/**
 * Calculate AP/BP scale percentage and severity.
 */
const getApBpScale = (entry) => {
    const normalizedDifference = entry.bp / entry.ap;
    const percentage = (normalizedDifference * 100).toFixed(2);

    const scale = [
        { min: 0, max: 65.00, severity: "Muito severa" },
        { min: 65.01, max: 68.00, severity: "Severa" },
        { min: 68.01, max: 71.00, severity: "Moderada" },
        { min: 71.01, max: 75.00, severity: "Leve" },
        { min: 75.01, max: 90.00, severity: "Normal" },
        { min: 90.01, max: 94.00, severity: "Leve" },
        { min: 94.01, max: 98.00, severity: "Moderada" },
        { min: 98.01, max: 102.00, severity: "Severa" },
        { min: 102.01, max: 9999999.99, severity: "Muito severa" },
    ];

    const found = scale.find(item => percentage >= item.min && percentage <= item.max);
    return { percentage, severity: found ? found.severity : "Desconhecido" };
};

/**
 * Calculate plagiocephaly (CVAI) percentage and severity.
 */
const getPlagiocephalyScale = (entry) => {
    const digMaior = entry.dd > entry.de ? entry.dd : entry.de;
    const digMenor = entry.dd < entry.de ? entry.dd : entry.de;
    const normalizedDifference = (digMaior - digMenor) / digMaior;
    const percentage = (normalizedDifference * 100).toFixed(2);

    const scale = [
        { min: 0, max: 3.49, severity: "Normal" },
        { min: 3.5, max: 6.25, severity: "Leve" },
        { min: 6.26, max: 8.75, severity: "Moderada" },
        { min: 8.76, max: 10.99, severity: "Severa" },
        { min: 11.00, max: 9999999.99, severity: "Muito severa" },
    ];

    const found = scale.find(item => percentage >= item.min && percentage <= item.max);
    return { percentage, severity: found ? found.severity : "Desconhecido" };
};

/**
 * Builds the generic reportData from cranial asymmetry history.
 * Uses the most recent entry for the summary.
 * 
 * @param {Array} history - Array of cranial asymmetry entries
 * @param {string} patientName - Patient name
 * @returns {object} Standardized reportData for clinicalReportService
 */
export const buildCranialAsymmetryReportData = (history, patientName) => {
    if (!history || history.length === 0) return null;

    // Sort by date descending to get most recent
    const sorted = [...history].sort((a, b) =>
        dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf()
    );
    const latest = sorted[0];

    const apBp = getApBpScale(latest);
    const plagio = getPlagiocephalyScale(latest);

    // Determine overall classification based on worst severity
    const severityOrder = ['Normal', 'Leve', 'Moderada', 'Severa', 'Muito severa'];
    const apBpIndex = severityOrder.indexOf(apBp.severity);
    const plagioIndex = severityOrder.indexOf(plagio.severity);
    const worstSeverity = apBpIndex >= plagioIndex ? apBp.severity : plagio.severity;

    return {
        title: 'Avaliação de assimetria craniana',
        patientName: patientName || '',
        assessmentDate: dayjs(latest.createdAt).format('DD/MM/YYYY'),
        measurements: [
            { label: 'AP', value: `${latest.ap} mm`, description: 'Ântero-Posterior' },
            { label: 'BP', value: `${latest.bp} mm`, description: 'Biparietal' },
            { label: 'Escala AP/BP', value: `${apBp.percentage}%`, severity: apBp.severity, isIndex: true },
            { label: 'DD', value: `${latest.dd} mm`, description: 'Diagonal Oblíqua' },
            { label: 'DE', value: `${latest.de} mm`, description: 'Diagonal Oblíqua' },
            { label: 'Plagiocefalia (CVAI)', value: `${plagio.percentage}%`, severity: plagio.severity, isIndex: true },
        ],
        classification: `Assimetria craniana ${worstSeverity.toLowerCase()}`,
    };
};

/**
 * Returns all history entries formatted for PDF display.
 * Marks the most recent entry with isLatest flag.
 * 
 * @param {Array} history - Array of cranial asymmetry entries
 * @returns {Array} Formatted entries with calculated values
 */
export const formatCranialAsymmetryHistory = (history) => {
    if (!history || history.length === 0) return [];

    const sorted = [...history]
        .sort((a, b) => dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf());

    return sorted.map((entry, index) => {
        const apBp = getApBpScale(entry);
        const plagio = getPlagiocephalyScale(entry);
        return {
            date: dayjs(entry.createdAt).format('DD/MM/YYYY'),
            ap: entry.ap,
            bp: entry.bp,
            apBpPercentage: apBp.percentage,
            apBpSeverity: apBp.severity,
            dd: entry.dd,
            de: entry.de,
            plagioPercentage: plagio.percentage,
            plagioSeverity: plagio.severity,
            isLatest: index === 0,
        };
    });
};

/**
 * Builds treatment summary data when there are multiple assessments.
 * Returns null if only one assessment exists.
 * 
 * @param {Array} history - Array of cranial asymmetry entries
 * @returns {object|null} Treatment summary or null
 */
export const buildTreatmentSummary = (history) => {
    if (!history || history.length <= 1) return null;

    const sorted = [...history].sort((a, b) =>
        dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf()
    );

    const firstDate = dayjs(sorted[0].createdAt);
    const lastDate = dayjs(sorted[sorted.length - 1].createdAt);

    // Calculate duration in months
    const diffMonths = lastDate.diff(firstDate, 'month');
    const diffDays = lastDate.diff(firstDate, 'day');

    let durationText;
    if (diffMonths >= 1) {
        durationText = diffMonths === 1 ? '1 mês' : `${diffMonths} meses`;
    } else {
        durationText = diffDays === 1 ? '1 dia' : `${diffDays} dias`;
    }

    return {
        firstDate: firstDate.format('DD/MM/YYYY'),
        lastDate: lastDate.format('DD/MM/YYYY'),
        duration: durationText,
        totalAssessments: history.length,
    };
};

/**
 * Builds measurement changes comparing first vs latest assessment.
 * Returns null if only one assessment exists.
 * Shows purely mathematical differences with direction indicators.
 * 
 * @param {Array} history - Array of cranial asymmetry entries
 * @returns {Array|null} Array of measurement change objects or null
 */
export const buildMeasurementChanges = (history) => {
    if (!history || history.length <= 1) return null;

    const sorted = [...history].sort((a, b) =>
        dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf()
    );

    const first = sorted[0];
    const latest = sorted[sorted.length - 1];

    const firstApBp = getApBpScale(first);
    const latestApBp = getApBpScale(latest);
    const firstPlagio = getPlagiocephalyScale(first);
    const latestPlagio = getPlagiocephalyScale(latest);

    const formatDiff = (diff, unit, decimals = 0) => {
        const sign = diff > 0 ? '+' : '';
        const value = decimals > 0 ? diff.toFixed(decimals) : diff;
        return `${sign}${value}${unit}`;
    };

    const apDiff = latest.ap - first.ap;
    const bpDiff = latest.bp - first.bp;
    const ddDiff = latest.dd - first.dd;
    const deDiff = latest.de - first.de;
    const apBpDiff = parseFloat(latestApBp.percentage) - parseFloat(firstApBp.percentage);
    const plagioDiff = parseFloat(latestPlagio.percentage) - parseFloat(firstPlagio.percentage);

    return [
        {
            label: 'AP',
            initialValue: `${first.ap} mm`,
            currentValue: `${latest.ap} mm`,
            difference: formatDiff(apDiff, ' mm'),
            direction: apDiff > 0 ? 'up' : apDiff < 0 ? 'down' : 'neutral',
        },
        {
            label: 'BP',
            initialValue: `${first.bp} mm`,
            currentValue: `${latest.bp} mm`,
            difference: formatDiff(bpDiff, ' mm'),
            direction: bpDiff > 0 ? 'up' : bpDiff < 0 ? 'down' : 'neutral',
        },
        {
            label: 'Escala AP/BP',
            initialValue: `${firstApBp.percentage}%`,
            currentValue: `${latestApBp.percentage}%`,
            difference: formatDiff(apBpDiff, '%', 2),
            direction: apBpDiff > 0 ? 'up' : apBpDiff < 0 ? 'down' : 'neutral',
        },
        {
            label: 'DD',
            initialValue: `${first.dd} mm`,
            currentValue: `${latest.dd} mm`,
            difference: formatDiff(ddDiff, ' mm'),
            direction: ddDiff > 0 ? 'up' : ddDiff < 0 ? 'down' : 'neutral',
        },
        {
            label: 'DE',
            initialValue: `${first.de} mm`,
            currentValue: `${latest.de} mm`,
            difference: formatDiff(deDiff, ' mm'),
            direction: deDiff > 0 ? 'up' : deDiff < 0 ? 'down' : 'neutral',
        },
        {
            label: 'Plagiocefalia (CVAI)',
            initialValue: `${firstPlagio.percentage}%`,
            currentValue: `${latestPlagio.percentage}%`,
            difference: formatDiff(plagioDiff, '%', 2),
            direction: plagioDiff > 0 ? 'up' : plagioDiff < 0 ? 'down' : 'neutral',
        },
    ];
};

/**
 * Uma ponta do par no relatório: sempre a versão `full` da foto, com a data da
 * medição correspondente.
 */
const buildPhotoItem = (item, urls) => {
    if (!item) return null;

    const size = 'full';

    return {
        id: item.photo.id,
        size: size,
        url: urls ? (urls[`${item.photo.id}:${size}`] || null) : null,
        date: dayjs(item.entry.createdAt).format('DD/MM/YYYY'),
    };
};

/**
 * Registro fotográfico do relatório: um par antes/depois por vista, com a mesma
 * regra usada no card (`buildBeforeAfterPairs`). Quando só existe uma medição
 * fotografada daquela vista, `before` vem null e o relatório mostra só o atual.
 *
 * Função pura: as object URLs entram pelo mapa `urls`, no formato
 * { "<id>:<size>": "blob:..." }, resolvido por loadReportPhotoUrls. Sem o mapa,
 * a estrutura sai com `url: null` e serve para listar o que precisa ser baixado.
 *
 * @param {Array} history medições de assimetria craniana
 * @param {Array} photos anexos de context 'cranial_photo'
 * @param {object|null} urls mapa de object URLs já carregadas
 * @returns {{pairs: Array}|null}
 */
export const buildCranialPhotoReport = (history, photos, urls = null) => {
    if (!history || history.length === 0) return null;

    const pairs = buildBeforeAfterPairs(history, photos).map(pair => ({
        view: pair.view,
        viewLabel: pair.viewLabel,
        before: buildPhotoItem(pair.before, urls),
        current: buildPhotoItem(pair.current, urls),
    }));

    if (pairs.length === 0) return null;

    return { pairs: pairs };
};

/**
 * Arquivos que o registro fotográfico do relatório precisa ter em memória
 * antes de gerar o PDF.
 *
 * @param {object|null} report saída de buildCranialPhotoReport
 * @returns {Array<{id: number, size: string}>} sem repetições
 */
export const listCranialReportPhotoRequests = (report) => {
    if (!report) return [];

    const seen = {};
    const requests = [];

    const add = (item) => {
        if (!item) return;

        const key = `${item.id}:${item.size}`;

        if (seen[key]) return;

        seen[key] = true;
        requests.push({ id: item.id, size: item.size });
    };

    (report.pairs || []).forEach((pair) => {
        add(pair.before);
        add(pair.current);
    });

    return requests;
};
